import { call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import {
    getBillingEntityMandates as getBillingEntityMandatesAction,
    getOperationCreditorsDetails as getOperationCreditorsDetailsAction,
    getOperationCreditorsForBillingNew as getOperationCreditorsForBillingNewAction,
    getProducerMandates as getProducerMandatesAction,
    setBillingEntityMandates,
    setDownloadLoading,
    setError,
    setOperationCreditorsDetails,
    setProducerMandates,
} from '../../redux/actions';
import {
    CREATE_BILLING_ENTITY_CREDITOR,
    CREATE_BILLING_ENTITY_MANDATE,
    CREATE_CREDITOR_DETAIL,
    CREATE_MULTIPLE_MANDATES_FOR_BILLING_ENTITY,
    CREATE_MULTIPLE_MANDATES_FOR_PRODUCER,
    CREATE_PARTICIPANT_MANDATES_DETAILS,
    DOWNLOAD_BILLING_ENTITY_SEPA_FILE,
    DOWNLOAD_SEPA_FILE,
    GET_BILLING_ENTITY_MANDATES,
    GET_OPERATION_CREDITORS_DETAILS,
    GET_OPERATION_CREDITORS_FOR_BILLING_NEW,
    GET_PRODUCER_MANDATES,
} from '../../redux/reducers/constants';
import {
    makeSelectConsumerName,
    makeSelectProducerName,
    selectProducers,
} from '../../redux/selectors/participantSelectors';
import i18n from 'i18next';
import {
    makeSelectBillingEntity,
    makeSelectDebitInvoicesByMonthAndBillingEntity,
    selectBillingEntities,
} from '../../redux/selectors/billingNewSelector';
import { errorHandler } from '../common/errorHandler';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

function* createCreditorDetail(action) {
    const { participantId, paymentDetail, isUpdate } = action;
    const paymentGateway = yield getContext('paymentGateway');
    try {
        yield call(
            paymentGateway.createParticipantPaymentDetail,
            participantId,
            paymentDetail
        );
        if (isUpdate) {
            yield put(getOperationCreditorsDetailsAction());
        }
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createBillingEntityCreditor(action) {
    const { billingEntityId, paymentDetail, isUpdate } = action;
    const paymentGateway = yield getContext('paymentGateway');
    try {
        yield call(
            paymentGateway.createBillingEntityPaymentDetail,
            billingEntityId,
            paymentDetail
        );
        if (isUpdate) {
            yield put(getOperationCreditorsForBillingNewAction());
        }
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createParticipantMandatesDetails(action) {
    const { participantId, mandatesDetails, isUpdate } = action;
    const paymentGateway = yield getContext('paymentGateway');
    try {
        yield call(
            paymentGateway.createParticipantMandatesDetails,
            participantId,
            mandatesDetails
        );
        if (isUpdate) {
            yield put(
                getProducerMandatesAction(Object.keys(mandatesDetails)[0])
            );
        }
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createBillingEntityMandate(action) {
    const { billingEntityId, mandateDetails, isUpdate } = action;
    const paymentGateway = yield getContext('paymentGateway');
    try {
        yield call(
            paymentGateway.createBillingEntityMandate,
            billingEntityId,
            mandateDetails
        );
        if (isUpdate) {
            yield put(getBillingEntityMandatesAction(billingEntityId));
        }
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createMultipleMandatesForProducer(action) {
    const { producerId, mandatesDetails } = action;
    const paymentGateway = yield getContext('paymentGateway');
    try {
        const results = yield call(
            paymentGateway.createMultipleMandatesForProducer,
            producerId,
            mandatesDetails
        );
        if (results.mandates && results.mandates.length > 0) {
            const mandates = yield call(
                paymentGateway.getProducerMandates,
                producerId
            );
            yield put(setBillingEntityMandates(mandates));
        }
        if (results.errors && results.errors.length > 0) {
            yield put(
                setError({
                    message:
                        'Erreurs pour les mandats :\n' +
                        results.errors
                            .map(
                                (error) =>
                                    `${error.mandateId} : ${i18n.t(
                                        error.message
                                    )}`
                            )
                            .join('\n'),
                })
            );
        }
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createMultipleMandatesForBillingEntity(action) {
    const { billingEntityId, mandatesDetails } = action;
    const paymentGateway = yield getContext('paymentGateway');
    try {
        const results = yield call(
            paymentGateway.createMultipleMandatesForBillingEntity,
            billingEntityId,
            mandatesDetails
        );
        if (results.mandates && results.mandates.length > 0) {
            const mandates = yield call(
                paymentGateway.getBillingEntityMandates,
                billingEntityId
            );
            yield put(setBillingEntityMandates(mandates));
        }
        if (results.errors && results.errors.length > 0) {
            yield put(
                setError({
                    message:
                        'Erreurs pour les mandats :\n' +
                        results.errors
                            .map(
                                (error) =>
                                    `${error.mandateId} : ${i18n.t(
                                        error.message
                                    )}`
                            )
                            .join('\n'),
                })
            );
        }
    } catch (error) {
        yield errorHandler(error);
    }
}

function* downloadMultipleSepaFiles(action) {
    try {
        const { billingEntity, periodEndMonth, invoices } = action;
        const paymentGateway = yield getContext('paymentGateway');
        const invoicesGroupedByProducers = invoices.reduce((acc, invoice) => {
            const producerId = invoice.producerId;
            if (!acc[producerId]) {
                acc[producerId] = [];
            }
            acc[producerId].push(invoice.id);
            return acc;
        }, {});

        const producerIds = Object.keys(invoicesGroupedByProducers);

        if (producerIds.length === 1) {
            const producerId = Number(producerIds[0]);
            const producerName = yield select(
                makeSelectProducerName(producerId)
            );
            yield call(
                paymentGateway.downloadSepaFile,
                producerId,
                invoicesGroupedByProducers[producerId],
                periodEndMonth,
                producerName
            );
        } else {
            const producers = [];
            for (let i = 0; i < producerIds.length; i++) {
                const producerId = Number(producerIds[i]);
                const producerName = yield select(
                    makeSelectProducerName(producerId)
                );
                producers.push({
                    producerId,
                    producerName,
                    invoicesIds: invoicesGroupedByProducers[producerId],
                });
            }
            yield put(setDownloadLoading(true));
            const urlsResponses = yield call(
                paymentGateway.getMultipleSepaDocumentsUrls,
                producers,
                periodEndMonth
            );
            const period = `f-${periodEndMonth}`;
            const zipFilename = `${period}-${billingEntity.name}.zip`;
            yield downloadZipFromContentObjects(urlsResponses, zipFilename);
        }
    } catch (error) {
        yield errorHandler(error);
    }
    yield put(setDownloadLoading(false));
}

const downloadZipFromContentObjects = async (urlObjects, zipFilename) => {
    const zip = new JSZip();
    await Promise.all(
        urlObjects.map(async (urlObject) => {
            const data = urlObject.content;
            zip.file(urlObject.title, data, { text: true });
        })
    );
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, zipFilename);
};

function* downloadBillingEntitySepaFile(action) {
    try {
        const { billingEntityId, periodEndMonth } = action;
        const debitInvoices = yield select(
            makeSelectDebitInvoicesByMonthAndBillingEntity(
                periodEndMonth,
                billingEntityId
            )
        );
        const legacyInvoices = debitInvoices.filter(
            (invoice) => invoice.isLegacyBill
        );
        const invoices = debitInvoices.filter(
            (invoice) => !invoice.isLegacyBill
        );
        const billingEntity = yield select(
            makeSelectBillingEntity(billingEntityId)
        );
        if (invoices.length) {
            const paymentGateway = yield getContext('paymentGateway');

            yield call(
                paymentGateway.downloadBillingEntitySepaFile,
                billingEntityId,
                invoices.map((invoice) => invoice.id),
                billingEntity.name,
                periodEndMonth
            );
        }
        if (legacyInvoices.length) {
            yield call(downloadMultipleSepaFiles, {
                billingEntity,
                invoices: legacyInvoices,
                periodEndMonth,
            });
        }
    } catch (error) {
        yield errorHandler(error);
    }
}
// DEPRECATED
function* downloadSepaFile(action) {
    try {
        const { producer, billingPeriodEnd } = action;
        const paymentGateway = yield getContext('paymentGateway');
        const producerName = yield select(makeSelectProducerName(producer.id));
        yield call(
            paymentGateway.downloadSepaFile,
            producer.id,
            [],
            billingPeriodEnd,
            producerName
        );
    } catch (error) {
        if (error.response?.data?.errors) {
            const missedCreditorsDetails = [];
            const missedDebtorsDetails = [];
            for (let i = 0; i < error.response.data.errors.length; i++) {
                const err = error.response.data.errors[i];
                const participantId = err.participantId;
                if (err.message === 'error.payment.mandate.not_found') {
                    const consumerName = yield select(
                        makeSelectConsumerName(participantId)
                    );
                    missedDebtorsDetails.push(consumerName);
                }
                if (err.message === 'error.payment.creditor.not_found') {
                    const producerName = yield select(
                        makeSelectProducerName(participantId)
                    );
                    missedCreditorsDetails.push(producerName);
                }
            }
            if (missedCreditorsDetails.length) {
                yield put(
                    setError({
                        message: `${i18n.t(
                            'enoapp.administrative.payment.creditorMissingDataMessage'
                        )}: ${missedCreditorsDetails.join(', ')}`,
                    })
                );
            }
            if (missedDebtorsDetails.length) {
                yield put(
                    setError({
                        message: `${i18n.t(
                            'enoapp.administrative.payment.debtorMissingDataMessage'
                        )}: ${missedDebtorsDetails.join(', ')}`,
                    })
                );
            }
        } else {
            yield errorHandler(error);
        }
    }
}

function* getOperationCreditorsDetails() {
    const producers = yield select(selectProducers);
    const paymentGateway = yield getContext('paymentGateway');
    try {
        const creditors = yield call(
            paymentGateway.getCreditorsDetailsByProducers,
            producers.map((producer) => producer.id)
        );
        yield put(setOperationCreditorsDetails(creditors));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getProducerMandates(action) {
    const { producerId } = action;
    const paymentGateway = yield getContext('paymentGateway');
    try {
        const mandates = yield call(
            paymentGateway.getProducerMandates,
            producerId
        );
        yield put(setProducerMandates(mandates));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getOperationCreditorsForBillingNew() {
    const billingEntities = yield select(selectBillingEntities);
    if (!billingEntities || billingEntities.length === 0) {
        return;
    }
    const paymentGateway = yield getContext('paymentGateway');
    try {
        const creditors = yield call(
            paymentGateway.getCreditorsDetailsByBillingEntities,
            billingEntities.map((entity) => entity.id)
        );
        yield put(setOperationCreditorsDetails(creditors));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getBillingEntityMandates(action) {
    const { billingEntityId } = action;
    const paymentGateway = yield getContext('paymentGateway');
    try {
        const mandates = yield call(
            paymentGateway.getBillingEntityMandates,
            billingEntityId
        );
        yield put(setBillingEntityMandates(mandates));
    } catch (error) {
        yield errorHandler(error);
    }
}

export function* createCreditorDetailSaga() {
    yield takeLatest(CREATE_CREDITOR_DETAIL, createCreditorDetail);
}

export function* createParticipantMandatesDetailsSaga() {
    yield takeLatest(
        CREATE_PARTICIPANT_MANDATES_DETAILS,
        createParticipantMandatesDetails
    );
}

export function* createBillingEntityCreditorSaga() {
    yield takeLatest(
        CREATE_BILLING_ENTITY_CREDITOR,
        createBillingEntityCreditor
    );
}

export function* createBillingEntityMandateSaga() {
    yield takeLatest(CREATE_BILLING_ENTITY_MANDATE, createBillingEntityMandate);
}

export function* createMultipleMandatesForProducerSaga() {
    yield takeLatest(
        CREATE_MULTIPLE_MANDATES_FOR_PRODUCER,
        createMultipleMandatesForProducer
    );
}

export function* createMultipleMandatesForBillingEntitySaga() {
    yield takeLatest(
        CREATE_MULTIPLE_MANDATES_FOR_BILLING_ENTITY,
        createMultipleMandatesForBillingEntity
    );
}

export function* downloadSepaFileSaga() {
    yield takeLatest(DOWNLOAD_SEPA_FILE, downloadSepaFile);
}

export function* getOperationCreditorsDetailsSaga() {
    yield takeLatest(
        GET_OPERATION_CREDITORS_DETAILS,
        getOperationCreditorsDetails
    );
}

export function* getOperationCreditorsForBillingNewSaga() {
    yield takeLatest(
        GET_OPERATION_CREDITORS_FOR_BILLING_NEW,
        getOperationCreditorsForBillingNew
    );
}

export function* getProducerMandatesSaga() {
    yield takeLatest(GET_PRODUCER_MANDATES, getProducerMandates);
}

export function* getBillingEntityMandatesSaga() {
    yield takeLatest(GET_BILLING_ENTITY_MANDATES, getBillingEntityMandates);
}

export function* downloadBillingEntitySepaFileSaga() {
    yield takeLatest(
        DOWNLOAD_BILLING_ENTITY_SEPA_FILE,
        downloadBillingEntitySepaFile
    );
}
const paymentSagas = [
    createCreditorDetailSaga,
    createParticipantMandatesDetailsSaga,
    createMultipleMandatesForProducerSaga,
    downloadSepaFileSaga,
    getOperationCreditorsDetailsSaga,
    getProducerMandatesSaga,
    createBillingEntityCreditorSaga,
    createBillingEntityMandateSaga,
    createMultipleMandatesForBillingEntitySaga,
    getBillingEntityMandatesSaga,
    getOperationCreditorsForBillingNewSaga,
    downloadBillingEntitySepaFileSaga,
];

export default paymentSagas;
